/**
 * Tela de login — `Lumma Fit Login.dc.html`.
 *
 * Uma tela, cinco views: login, recover_ask, recover_sent, recover_new e
 * created. A troca é de estado, não de rota, exatamente como o protótipo — a
 * URL só muda quando o usuário sai do fluxo de entrada.
 */
import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { Faixa, LinkTexto } from '../../componentes/Basicos'
import { Botao } from '../../componentes/Botao'
import { Caixa, Campo, CampoSenha } from '../../componentes/Campos'
import { CampoCodigo } from '../../componentes/CampoCodigo'
import { ListaDeBeneficios, PainelLateral } from '../../componentes/PainelLateral'
import { api, ErroApi } from '../../plataforma/api'
import { useSessao } from '../../plataforma/contexto-sessao'
import type { Sessao } from '../../plataforma/contexto-sessao'

type View = 'login' | 'recuperar' | 'enviado' | 'nova_senha' | 'criada'

type Feedback = { tipo: 'sucesso' | 'erro'; texto: string } | null

const BENEFICIOS = [
  'Estoque em grade por cor e numeração',
  'PDV, fiscal e financeiro integrados',
  'Alertas de ruptura por tamanho',
]

export function TelaLogin() {
  const navegar = useNavigate()
  const local = useLocation()
  const { definir } = useSessao()

  // Quem acabou de criar a conta chega aqui com o estado da navegação.
  const estadoDeNavegacao = local.state as { contaCriada?: string } | null

  const [view, setView] = useState<View>(estadoDeNavegacao?.contaCriada ? 'criada' : 'login')
  const [feedback, setFeedback] = useState<Feedback>(null)

  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [manter, setManter] = useState(false)
  const [entrando, setEntrando] = useState(false)

  const [emailRecuperacao, setEmailRecuperacao] = useState('')
  const [codigo, setCodigo] = useState('')
  const [novaSenha, setNovaSenha] = useState('')
  const [repetirSenha, setRepetirSenha] = useState('')

  const [emailCriado] = useState(estadoDeNavegacao?.contaCriada ?? '')
  const [reenvios, setReenvios] = useState(0)

  function irPara(proxima: View) {
    setView(proxima)
    setFeedback(null)
  }

  async function entrar(evento: FormEvent) {
    evento.preventDefault()

    // A validação da tela é a do protótipo. A do servidor é mais rígida, e é
    // ela que decide — esta só evita uma ida à rede à toa.
    if (!email.includes('@') || senha.length < 4) {
      setFeedback({ tipo: 'erro', texto: 'Informe um e-mail válido e a senha da loja.' })
      return
    }

    setEntrando(true)
    setFeedback(null)
    try {
      const sessao = await api.post<Sessao>('/sessoes', {
        email,
        senha,
        manter_conectado: manter,
      })
      definir(sessao)
      setFeedback({ tipo: 'sucesso', texto: 'Acesso liberado. Abrindo o sistema…' })

      // Primeiro acesso vai para o onboarding; os demais, direto ao trabalho.
      navegar(sessao.primeiro_acesso ? '/primeiro-acesso' : '/app', { replace: true })
    } catch (erro) {
      setFeedback({ tipo: 'erro', texto: mensagemDe(erro) })
    } finally {
      setEntrando(false)
    }
  }

  async function pedirRecuperacao(evento?: FormEvent) {
    evento?.preventDefault()

    if (!emailRecuperacao.includes('@')) {
      setFeedback({ tipo: 'erro', texto: 'Informe um e-mail válido.' })
      return
    }

    try {
      await api.post('/senha/recuperacoes', { email: emailRecuperacao })
      irPara('enviado')
    } catch (erro) {
      setFeedback({ tipo: 'erro', texto: mensagemDe(erro) })
    }
  }

  async function salvarNovaSenha(evento: FormEvent) {
    evento.preventDefault()

    if (novaSenha.length < 8) {
      setFeedback({ tipo: 'erro', texto: 'A senha precisa de 8 caracteres ou mais.' })
      return
    }
    if (novaSenha !== repetirSenha) {
      setFeedback({ tipo: 'erro', texto: 'As senhas não coincidem.' })
      return
    }

    try {
      await api.post('/senha/redefinicoes', {
        email: emailRecuperacao,
        codigo,
        nova_senha: novaSenha,
        repetir_senha: repetirSenha,
      })
      setFeedback({ tipo: 'sucesso', texto: 'Senha alterada. Você já pode entrar.' })
      setTimeout(() => irPara('login'), 1200)
    } catch (erro) {
      setFeedback({ tipo: 'erro', texto: mensagemDe(erro) })

      // Código recusado volta para a tela do código, não deixa a pessoa
      // presa no formulário de senha sem entender o que fazer.
      if (erro instanceof ErroApi && erro.campos['codigo']) {
        setCodigo('')
        irPara('enviado')
        setFeedback({ tipo: 'erro', texto: mensagemDe(erro) })
      }
    }
  }

  async function reenviarConfirmacao() {
    try {
      await api.post('/email/reenvios', { email: emailCriado })
      setReenvios((atual) => Math.min(3, atual + 1))
    } catch (erro) {
      if (erro instanceof ErroApi && erro.codigo === 'muitas_tentativas') {
        setReenvios(3)
      }
      setFeedback({ tipo: 'erro', texto: mensagemDe(erro) })
    }
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.06fr_1fr]">
      <PainelLateral
        titulo="A loja inteira em uma única tela."
        subtitulo="Estoque em grade, PDV, fiscal e financeiro no mesmo lugar."
      >
        <ListaDeBeneficios itens={BENEFICIOS} />
      </PainelLateral>

      <div className="flex items-center justify-center px-10 py-16">
        <div className="w-full max-w-[396px]">
          {view === 'login' && (
            <form onSubmit={entrar} noValidate>
              <h1 className="mb-2 font-titulo text-[28px] font-bold text-titulo">
                Entrar no sistema
              </h1>
              <p className="mb-[30px] text-sm text-texto-suave">
                Use o e-mail cadastrado pela sua loja.
              </p>

              <div className="mb-[18px]">
                <Campo
                  variante="auth"
                  rotulo="E-mail"
                  type="email"
                  autoComplete="username"
                  placeholder="nome@sualoja.com.br"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    setFeedback(null)
                  }}
                />
              </div>

              <div className="mb-5">
                <CampoSenha
                  variante="auth"
                  rotulo="Senha"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={senha}
                  onChange={(e) => {
                    setSenha(e.target.value)
                    setFeedback(null)
                  }}
                  acessorio={
                    <LinkTexto tamanho={13} aoClicar={() => irPara('recuperar')}>
                      Esqueci minha senha
                    </LinkTexto>
                  }
                />
              </div>

              <div className="mb-[26px]">
                <Caixa marcada={manter} aoMudar={setManter}>
                  Manter-me conectado
                </Caixa>
              </div>

              <Botao type="submit" porte="auth" altura={54} larguraTotal disabled={entrando}>
                {entrando ? 'Entrando…' : 'Entrar'}
              </Botao>

              <p className="mt-6 text-[13.5px] text-texto-suave">
                Sua loja ainda não usa o Lumma Store?{' '}
                <Link to="/criar-conta" className="font-semibold text-marca hover:text-marca-forte">
                  Criar conta
                </Link>
              </p>
            </form>
          )}

          {view === 'recuperar' && (
            <form onSubmit={pedirRecuperacao} noValidate>
              <button
                type="button"
                onClick={() => irPara('login')}
                className="mb-[22px] inline-flex items-center gap-[6px] text-[13px] font-semibold text-texto-suave"
              >
                ← Voltar para o login
              </button>

              <h1 className="mb-2 font-titulo text-[28px] font-bold text-titulo">
                Recuperar senha
              </h1>
              <p className="mb-[26px] text-sm leading-[1.65] text-texto-suave">
                Informe o e-mail cadastrado e enviaremos um link para redefinir a senha.
              </p>

              <div className="mb-5">
                <Campo
                  variante="auth"
                  rotulo="E-mail"
                  type="email"
                  placeholder="nome@sualoja.com.br"
                  value={emailRecuperacao}
                  onChange={(e) => {
                    setEmailRecuperacao(e.target.value)
                    setFeedback(null)
                  }}
                />
              </div>

              <Botao type="submit" porte="auth" larguraTotal>
                Enviar link
              </Botao>
            </form>
          )}

          {view === 'enviado' && (
            <div>
              <div className="text-center">
                <div
                  aria-hidden
                  className="mx-auto mb-[22px] flex h-[58px] w-[58px] items-center justify-center rounded-full bg-marca-suave text-[22px] font-bold text-marca"
                >
                  ✉
                </div>
                <h1 className="mb-[10px] font-titulo text-[26px] font-bold text-titulo">
                  Código enviado
                </h1>
                <p className="mb-6 text-sm leading-[1.65] text-texto-suave">
                  Se existir uma conta para{' '}
                  <strong className="text-titulo">{emailRecuperacao || 'seu e-mail'}</strong>,
                  o código de 6 dígitos chega em alguns minutos. Ele expira em 15 minutos.
                </p>
              </div>

              {/*
                O código é digitado AQUI, na aba onde a pessoa pediu a troca.
                É a razão de a spec 06 usar código e não link: o pedido sai do
                computador do balcão e o e-mail abre no celular. Com link, ela
                teria que continuar no celular.
              */}
              <div className="mb-5">
                <CampoCodigo valor={codigo} aoMudar={setCodigo} />
              </div>

              <Botao porte="auth" larguraTotal disabled={codigo.length < 6} onClick={() => irPara('nova_senha')}>
                Continuar
              </Botao>

              <div className="mt-4 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => void pedirRecuperacao()}
                  className="text-[13px] font-semibold text-marca hover:text-marca-forte"
                >
                  Reenviar código
                </button>
                <button
                  type="button"
                  onClick={() => irPara('login')}
                  className="text-[13px] font-semibold text-texto-suave"
                >
                  Voltar para o login
                </button>
              </div>
            </div>
          )}

          {view === 'nova_senha' && (
            <form onSubmit={salvarNovaSenha} noValidate>
              <h1 className="mb-2 font-titulo text-[28px] font-bold text-titulo">
                Definir nova senha
              </h1>
              <p className="mb-[26px] text-sm leading-[1.65] text-texto-suave">
                Use 8 caracteres ou mais. Evite a mesma senha de outros sistemas.
              </p>

              <CampoSenha
                variante="auth"
                rotulo="Nova senha"
                autoComplete="new-password"
                placeholder="••••••••"
                comMedidor
                value={novaSenha}
                onChange={(e) => {
                  setNovaSenha(e.target.value)
                  setFeedback(null)
                }}
              />

              <div className="mb-5">
                <CampoSenha
                  variante="auth"
                  rotulo="Repetir senha"
                  autoComplete="new-password"
                  placeholder="••••••••"
                  value={repetirSenha}
                  onChange={(e) => {
                    setRepetirSenha(e.target.value)
                    setFeedback(null)
                  }}
                />
              </div>

              <Botao type="submit" porte="auth" larguraTotal>
                Salvar nova senha
              </Botao>

              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => irPara('login')}
                  className="text-[13.5px] font-semibold text-texto-suave"
                >
                  Voltar para o login
                </button>
              </div>
            </form>
          )}

          {view === 'criada' && (
            <div className="text-center">
              <div
                aria-hidden
                className="mx-auto mb-[22px] flex h-[58px] w-[58px] items-center justify-center rounded-full bg-ok-suave text-2xl font-bold text-ok-texto"
              >
                ✓
              </div>
              <h1 className="mb-[10px] font-titulo text-[26px] font-bold text-titulo">
                Conta criada
              </h1>
              <p className="mb-6 text-sm leading-[1.65] text-pretty text-texto-suave">
                Enviamos um link de confirmação para{' '}
                <strong className="text-titulo">{emailCriado}</strong>. Confirme o e-mail para
                liberar a emissão fiscal.
              </p>

              <div className="mb-5 rounded-card border border-borda p-[18px] text-left">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <span className="text-[13px] font-semibold text-titulo">
                    Não recebeu o e-mail?
                  </span>
                  <span className="text-xs text-texto-fraco">
                    {reenvios > 0 ? `Reenviado ${reenvios}x` : 'Aguarde alguns minutos'}
                  </span>
                </div>

                <Botao
                  variante="secundario"
                  porte="auth"
                  altura={44}
                  larguraTotal
                  disabled={reenvios >= 3}
                  onClick={reenviarConfirmacao}
                >
                  {reenvios >= 3 ? 'Limite de reenvios atingido' : 'Reenviar e-mail de confirmação'}
                </Botao>

                <p className="mt-[10px] text-[12.5px] leading-[1.6] text-texto-fraco">
                  Verifique a caixa de spam. O link expira em 24 horas.
                </p>
              </div>

              <Botao porte="auth" larguraTotal onClick={() => irPara('login')}>
                Ir para o login
              </Botao>
            </div>
          )}

          {feedback && <Faixa tipo={feedback.tipo}>{feedback.texto}</Faixa>}
        </div>
      </div>
    </div>
  )
}

function mensagemDe(erro: unknown): string {
  if (erro instanceof ErroApi) return erro.message
  return 'Não foi possível concluir a operação. Tente novamente.'
}
